import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Shield, Users, MessageCircle, TrendingUp, Star, Download, Apple } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const DownloadApp = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: Users,
      title: 'Community-Focused',
      description: 'Create private groups for family, friends, colleagues, or your neighborhood'
    },
    {
      icon: Shield,
      title: 'Secure Payments',
      description: 'Safe transactions with Paystack escrow protection for peace of mind'
    },
    {
      icon: MessageCircle,
      title: 'Direct Messaging',
      description: 'Chat with buyers and sellers instantly within the app'
    },
    {
      icon: TrendingUp,
      title: 'Local Treasures',
      description: 'Discover hidden gems from neighbors in your community'
    }
  ];

  const testimonials = [
    {
      name: 'Chioma N.',
      location: 'Lagos',
      text: 'I sold my old furniture to my neighbor in just 2 days! The private group feature is perfect for our estate.',
      rating: 5
    },
    {
      name: 'Tunde A.',
      location: 'Abuja',
      text: 'Finally, a marketplace I can trust. Buying from people in my community makes all the difference.',
      rating: 5
    },
    {
      name: 'Fatima Y.',
      location: 'Port Harcourt',
      text: 'The family group feature is amazing! We share items we no longer need instead of letting them gather dust.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 text-white py-20">
        {/* Adire Pattern Overlay */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'url(https://images.stockcake.com/public/3/5/a/35acffca-534f-4130-b59d-ce7850c95b76_large/indigo-fabric-artistry-stockcake.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="mb-8">
                <img 
                  src="/neighbour-sale-logo.png" 
                  alt="Neighbour's Sale" 
                  className="h-20 w-auto mb-6"
                />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 drop-shadow-lg">
                Sell or Swap...
                <br />Buy Neighbour
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-50 drop-shadow">
                Home or personal yard sale made easy. Download the app and start trading with your community today!
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-gray-100 font-semibold shadow-xl text-lg px-8 py-6"
                  onClick={() => window.open('https://play.google.com/store', '_blank')}
                >
                  <Download className="mr-2 w-6 h-6" />
                  Download on Google Play
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold shadow-xl text-lg px-8 py-6"
                  onClick={() => window.open('https://apps.apple.com', '_blank')}
                >
                  <Apple className="mr-2 w-6 h-6" />
                  Download on App Store
                </Button>
              </div>
              <p className="text-sm text-blue-100 mt-4">
                Or use the web version: <button onClick={() => navigate('/')} className="underline font-semibold hover:text-white">Browse Now</button>
              </p>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                {/* Phone mockup - you can replace with actual app screenshots */}
                <div className="relative mx-auto" style={{ width: '300px' }}>
                  <img
                    src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=800&fit=crop"
                    alt="App Preview"
                    className="rounded-3xl shadow-2xl border-8 border-gray-900"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-600/30 to-transparent rounded-3xl" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Neighbour&apos;s Sale?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The only marketplace built for community. Sell to people you can trust.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card key={feature.title} className="border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 mb-4 shadow-lg">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Simple steps to start buying and selling</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-600 text-white text-3xl font-bold mb-6 shadow-lg">
                1
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Download & Sign Up</h3>
              <p className="text-gray-600 text-lg">
                Get the app from Google Play or App Store. Create your account in seconds.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-600 text-white text-3xl font-bold mb-6 shadow-lg">
                2
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Join or Create Groups</h3>
              <p className="text-gray-600 text-lg">
                Connect with your community, family, or colleagues through private groups.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-blue-600 text-white text-3xl font-bold mb-6 shadow-lg">
                3
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Start Trading</h3>
              <p className="text-gray-600 text-lg">
                List items, browse treasures, and buy from trusted neighbors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Community Says</h2>
            <p className="text-xl text-gray-600">Join thousands of happy neighbors</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.name} className="border-2 border-blue-100">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`${testimonial.name}-star-${i}`} className="w-5 h-5 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">&quot;{testimonial.text}&quot;</p>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'url(https://images.stockcake.com/public/2/2/f/22ff820b-ec16-4104-b1e6-1c9390bdefd3_large/indigo-textile-artistry-stockcake.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">10K+</div>
              <div className="text-xl text-blue-100">Active Users</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-xl text-blue-100">Communities</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">50K+</div>
              <div className="text-xl text-blue-100">Items Traded</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">4.8★</div>
              <div className="text-xl text-blue-100">App Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Smartphone className="w-20 h-20 mx-auto mb-6 text-blue-400" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Start Trading with Your Neighbors?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Download Neighbour&apos;s Sale now and discover treasures in your community
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 font-semibold text-lg px-8 py-6"
              onClick={() => window.open('https://play.google.com/store', '_blank')}
            >
              <Download className="mr-2 w-6 h-6" />
              Get it on Google Play
            </Button>
            <Button
              size="lg"
              className="bg-white text-gray-900 hover:bg-gray-100 font-semibold text-lg px-8 py-6"
              onClick={() => window.open('https://apps.apple.com', '_blank')}
            >
              <Apple className="mr-2 w-6 h-6" />
              Download on App Store
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadApp;